import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { X, Loader2, Play, Check, ChevronsUpDown, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const INITIAL_FORM = {
  email: '', name: '', company_name: '', jurisdiction: 'jamaica',
  industry: '', phone: '', internal_notes: '',
  status: 'engaged', expires_in_days: 1,
};

export const InPersonAssessmentModal = ({ open, onClose, headers, onCreated, existingClients = [] }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [selectedClientEmail, setSelectedClientEmail] = useState('');

  // Dedupe existingClients by email (most recent first); keep latest non-empty fields per client
  const uniqueClients = useMemo(() => {
    const byEmail = new Map();
    for (const inv of existingClients) {
      if (!inv.email) continue;
      const existing = byEmail.get(inv.email);
      if (!existing || new Date(inv.created_at) > new Date(existing.created_at)) {
        byEmail.set(inv.email, inv);
      }
    }
    return Array.from(byEmail.values()).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [existingClients]);

  if (!open) return null;

  const handleSelectClient = (email) => {
    const client = uniqueClients.find((c) => c.email === email);
    if (!client) return;
    setSelectedClientEmail(email);
    setForm((prev) => ({
      ...prev,
      email: client.email || '',
      name: client.name || prev.name,
      company_name: client.company_name || '',
      jurisdiction: client.jurisdiction || prev.jurisdiction,
      industry: client.industry || '',
      phone: client.phone || '',
      // Don't auto-copy internal notes — those are per-engagement
    }));
    setClientPickerOpen(false);
    toast.success(`Pre-filled from ${client.company_name || client.name || client.email}`);
  };

  const resetClientSelection = () => {
    setSelectedClientEmail('');
    setForm(INITIAL_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.company_name || !form.jurisdiction) {
      toast.error('Please fill in client name, email, company, and jurisdiction');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/admin/invitations', form, { headers });
      const link = response.data.invitation_link;

      // Open guest assessment in a new tab immediately
      const win = window.open(link, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.warning('Pop-up blocked. Link copied to clipboard — paste it in a new tab.');
        await navigator.clipboard.writeText(link);
      } else {
        toast.success(`In-person assessment ready for ${form.name}`);
      }

      onCreated?.(response.data);
      setForm(INITIAL_FORM);
      setSelectedClientEmail('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start in-person assessment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="inperson-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Play className="h-5 w-5 text-[#10B981]" />
              Start In-Person Assessment
            </h2>
            <p className="text-slate-400 text-xs mt-1">
              Fill in your client's details and we'll open the assessment in a new tab right away. No email is sent.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-inperson-modal">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Existing client picker */}
          {uniqueClients.length > 0 && (
            <div className="bg-slate-700/30 border border-slate-700 rounded-lg p-3" data-testid="existing-client-picker">
              <Label className="text-slate-300 text-xs mb-2 block flex items-center gap-2">
                <UserPlus className="h-3 w-3" />
                Returning client? Pre-fill from history
              </Label>
              <div className="flex gap-2">
                <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      data-testid="open-existing-client-picker"
                      className="flex-1 justify-between bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-700/80"
                    >
                      {selectedClientEmail
                        ? (() => {
                            const c = uniqueClients.find((x) => x.email === selectedClientEmail);
                            return c ? `${c.company_name || c.name || c.email}` : 'Select existing client…';
                          })()
                        : `Search ${uniqueClients.length} past client${uniqueClients.length === 1 ? '' : 's'}…`}
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-slate-800 border-slate-600">
                    <Command className="bg-slate-800">
                      <CommandInput placeholder="Search by name, company, or email…" className="text-white" />
                      <CommandList>
                        <CommandEmpty>No client matches.</CommandEmpty>
                        <CommandGroup>
                          {uniqueClients.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.name || ''} ${c.company_name || ''} ${c.email}`}
                              onSelect={() => handleSelectClient(c.email)}
                              data-testid={`existing-client-${c.email}`}
                              className="text-slate-200 cursor-pointer"
                            >
                              <Check className={`mr-2 h-4 w-4 ${selectedClientEmail === c.email ? 'opacity-100' : 'opacity-0'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">{c.company_name || c.name || c.email}</p>
                                <p className="text-xs text-slate-500 truncate">
                                  {c.name && c.company_name ? `${c.name} • ` : ''}{c.email}
                                  {c.jurisdiction && ` • ${c.jurisdiction}`}
                                </p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedClientEmail && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetClientSelection}
                    className="text-slate-400 hover:text-white"
                    data-testid="reset-client-selection"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {selectedClientEmail && (
                <p className="text-xs text-[#10B981] mt-2">
                  ✓ Pre-filled — edit any field below before starting. Jurisdiction defaults to their last one; change if needed.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Client Name *</Label>
              <Input
                data-testid="inperson-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Smith"
                required
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                data-testid="inperson-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="client@company.com"
                required
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Company *</Label>
              <Input
                data-testid="inperson-company"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="Acme Corp"
                required
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Jurisdiction *</Label>
              <Select
                value={form.jurisdiction}
                onValueChange={(v) => setForm({ ...form, jurisdiction: v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="inperson-jurisdiction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jamaica">Jamaica</SelectItem>
                  <SelectItem value="barbados">Barbados</SelectItem>
                  <SelectItem value="bermuda">Bermuda</SelectItem>
                  <SelectItem value="cayman">Cayman Islands</SelectItem>
                  <SelectItem value="bahamas">The Bahamas</SelectItem>
                  <SelectItem value="belize">Belize</SelectItem>
                  <SelectItem value="grenada">Grenada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Industry</Label>
              <Select
                value={form.industry || ''}
                onValueChange={(v) => setForm({ ...form, industry: v })}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="inperson-industry">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Financial Services">Financial Services</SelectItem>
                  <SelectItem value="Healthcare">Healthcare</SelectItem>
                  <SelectItem value="Telecoms">Telecoms</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Retail">Retail</SelectItem>
                  <SelectItem value="Government">Government</SelectItem>
                  <SelectItem value="Hospitality">Hospitality</SelectItem>
                  <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="Professional Services">Professional Services</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Non-profit">Non-profit</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                data-testid="inperson-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1-868-555-1234"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Internal Notes (private)</Label>
            <textarea
              data-testid="inperson-notes"
              value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
              placeholder="Context from this meeting, follow-up flags..."
              rows={2}
              className="w-full bg-slate-700 border border-slate-600 text-white mt-1 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]"
            />
          </div>

          <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-3 text-sm text-slate-300">
            ⚡ This will create an invitation, mark it as <strong className="text-white">Engaged</strong>,
            set expiry to <strong className="text-white">1 day</strong>, and open the assessment in a new tab.
            No email is sent.
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="inperson-start-btn"
              disabled={submitting}
              className="bg-[#10B981] hover:bg-[#059669] text-white"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Start Assessment in New Tab
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
